import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type Answer, type OnboardingState } from "../api";
import { useSession } from "../session";
import { Choice, ErrorNotice, Loading, Mark } from "../ui";

const STEPS = ["welcome", "start_year", "about", "situation", "areas", "accountant", "history", "ready"] as const;
type Step = (typeof STEPS)[number];

const YES_NO: { value: Answer; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "not_sure", label: "Not sure" },
];

export function Onboarding() {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [step, setStep] = useState<Step>("welcome");
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<OnboardingState>("/api/onboarding").then((s) => {
      setState(s);
      setStep((STEPS as readonly string[]).includes(s.step) ? (s.step as Step) : "welcome");
    }).catch(setError);
  }, []);

  if (!state) return <div className="center-page"><ErrorNotice error={error} /><Loading /></div>;

  const index = STEPS.indexOf(step);
  const go = async (to: Step, save?: () => Promise<OnboardingState>) => {
    setSaving(true);
    setError(null);
    try {
      if (save) setState(await save());
      setState(await api.put<OnboardingState>("/api/onboarding/step", { step: to }));
      setStep(to);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };
  const next = (save?: () => Promise<OnboardingState>) => go(STEPS[index + 1]!, save);
  const back = index > 0 ? () => go(STEPS[index - 1]!) : undefined;
  const props = { state, saving, next, back, error };

  return (
    <div className="main">
      <div className="wizard stack" style={{ "--gap": "28px" } as React.CSSProperties}>
        <div className="row"><Mark /><strong>TaxApp</strong><div className="spacer" /><span className="muted small">Step {index + 1} of {STEPS.length}</span></div>
        <div className="progress" aria-hidden><div style={{ width: `${((index + 1) / STEPS.length) * 100}%` }} /></div>
        {step === "welcome" && <Welcome {...props} />}
        {step === "start_year" && <StartYear {...props} />}
        {step === "about" && <About {...props} />}
        {step === "situation" && <Situation {...props} />}
        {step === "areas" && <Areas {...props} />}
        {step === "accountant" && <Accountant {...props} />}
        {step === "history" && <History {...props} />}
        {step === "ready" && <Ready {...props} />}
      </div>
    </div>
  );
}

interface StepProps {
  state: OnboardingState;
  saving: boolean;
  error: unknown;
  next: (save?: () => Promise<OnboardingState>) => Promise<void>;
  back?: () => Promise<void>;
}

function Actions({ saving, back, onContinue, continueLabel = "Continue", canContinue = true, skip }: {
  saving: boolean;
  back?: () => void;
  onContinue: () => void;
  continueLabel?: string;
  canContinue?: boolean;
  skip?: () => void;
}) {
  return (
    <div className="wizard-actions">
      {back && <button type="button" className="btn ghost" onClick={back} disabled={saving}>Back</button>}
      <div className="spacer" />
      {skip && <button type="button" className="btn ghost" onClick={skip} disabled={saving}>Skip for now</button>}
      <button type="button" className="btn primary" onClick={onContinue} disabled={saving || !canContinue}>{continueLabel}</button>
    </div>
  );
}

function Welcome({ saving, next }: StepProps) {
  return (
    <section className="stack" style={{ "--gap": "16px" } as React.CSSProperties}>
      <h1>Welcome</h1>
      <p>TaxApp keeps your receipts, income and investments in one place, works out each year under that year's tax rules, and gives your accountant a ready-to-use pack at tax time.</p>
      <div className="card flat stack">
        <h3>Your data stays private</h3>
        <ul className="muted" style={{ margin: 0, paddingLeft: 20 }}>
          <li>It's stored on your own server, encrypted, and backed up.</li>
          <li>Only you, and people you choose, can see your records.</li>
          <li>Nothing is sent to other services unless you turn that on.</li>
        </ul>
      </div>
      <p className="muted">Setting up takes about five minutes. You can skip anything and finish it later.</p>
      <Actions saving={saving} onContinue={() => next()} continueLabel="Get started" />
    </section>
  );
}

function StartYear({ state, saving, next, back, error }: StepProps) {
  const [fy, setFy] = useState(state.startYear ?? state.yearChoices[0]?.fy ?? "");
  const locked = Boolean(state.startYear) && state.about.residency !== null;
  return (
    <section className="stack" style={{ "--gap": "16px" } as React.CSSProperties}>
      <h1>Which year do you want to start from?</h1>
      <p className="muted">Most people start with the current financial year. Years you've already lodged can be added later from your past returns.</p>
      <div className="field">
        <label htmlFor="fy">Financial year</label>
        <select id="fy" value={fy} onChange={(e) => setFy(e.target.value)} disabled={locked}>
          {state.yearChoices.map((y, i) => (
            <option key={y.fy} value={y.fy}>{y.label}{i === 0 ? " (this year)" : ""}</option>
          ))}
        </select>
        {locked && <span className="hint">This can't change once you've answered the next questions.</span>}
      </div>
      <ErrorNotice error={error} />
      <Actions saving={saving} back={back} canContinue={Boolean(fy)}
        onContinue={() => next(() => api.put<OnboardingState>("/api/onboarding/start-year", { fy }))} />
    </section>
  );
}

function About({ state, saving, next, back, error }: StepProps) {
  const [name, setName] = useState(state.about.displayName);
  const [dob, setDob] = useState(state.about.dateOfBirth ?? "");
  const [residency, setResidency] = useState(state.about.residency);
  const [occupation, setOccupation] = useState(state.about.occupation ?? "");
  return (
    <section className="stack" style={{ "--gap": "16px" } as React.CSSProperties}>
      <h1>About you</h1>
      <div className="field">
        <label htmlFor="name">What should we call you?</label>
        <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="dob">Date of birth</label>
        <input id="dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        <span className="why">Some offsets, health insurance rebates and super rules depend on your age.</span>
      </div>
      <div className="question">
        <span className="label">Are you an Australian resident for tax purposes?</span>
        <Choice label="Residency" value={residency as "resident" | "foreign_resident" | "part_year" | "not_sure" | null}
          onChange={setResidency}
          options={[
            { value: "resident", label: "Yes" },
            { value: "foreign_resident", label: "No" },
            { value: "part_year", label: "Part of the year" },
            { value: "not_sure", label: "Not sure" },
          ]} />
        <span className="why">Residency decides which tax rates apply. Most people living and working in Australia are residents.</span>
      </div>
      <div className="field">
        <label htmlFor="occupation">Occupation <span className="muted">(optional)</span></label>
        <input id="occupation" type="text" value={occupation} onChange={(e) => setOccupation(e.target.value)} placeholder="e.g. Nurse, Electrician, Teacher" />
        <span className="why">Your return asks for it, and it helps us suggest the deductions that apply to your work.</span>
      </div>
      <ErrorNotice error={error} />
      <Actions saving={saving} back={back} canContinue={Boolean(name.trim() && residency)}
        onContinue={() => next(() => api.put<OnboardingState>("/api/onboarding/about", {
          displayName: name.trim(), dateOfBirth: dob || null, residency, occupation: occupation.trim() || null,
        }))} />
    </section>
  );
}

function Situation({ state, saving, next, back, error }: StepProps) {
  const [answers, setAnswers] = useState(state.situation);
  const set = (key: keyof OnboardingState["situation"], answer: Answer) => setAnswers({ ...answers, [key]: { ...answers[key], answer } });
  const questions: { key: keyof OnboardingState["situation"]; question: string; why: string }[] = [
    { key: "spouse", question: "Do you have a spouse or partner?", why: "Married or de facto. Their income affects the Medicare levy surcharge and health insurance rebate." },
    { key: "dependants", question: "Do you have dependent children?", why: "Children you support can lower the Medicare levy and change income thresholds." },
    { key: "privateHospitalCover", question: "Do you have private hospital cover?", why: "Without it, higher earners pay the Medicare levy surcharge." },
    { key: "studyLoan", question: "Do you have a HECS-HELP or other study loan?", why: "Shown in myGov as HELP, VSL or another study and training loan. Repayments come out through your return." },
    { key: "medicareExemption", question: "Are you exempt from the Medicare levy?", why: "Rare — for example, some foreign residents or people with certain medical entitlements." },
  ];
  const complete = questions.every((q) => answers[q.key].answer);
  return (
    <section className="stack" style={{ "--gap": "8px" } as React.CSSProperties}>
      <h1>Your situation</h1>
      <p className="muted">These apply from the start of your first year. You can update them whenever things change.</p>
      <div>
        {questions.map((q) => (
          <div className="question" key={q.key}>
            <span className="label">{q.question}</span>
            <Choice label={q.question} options={YES_NO} value={answers[q.key].answer} onChange={(a) => set(q.key, a)} />
            {q.key === "dependants" && answers.dependants.answer === "yes" && (
              <div className="field" style={{ maxWidth: 180 }}>
                <label htmlFor="children">How many?</label>
                <input id="children" type="number" min={1} max={20} value={answers.dependants.count ?? 1}
                  onChange={(e) => setAnswers({ ...answers, dependants: { ...answers.dependants, count: Number(e.target.value) } })} />
              </div>
            )}
            <span className="why">{q.why}</span>
          </div>
        ))}
      </div>
      <p className="muted small">"Not sure" is fine — we'll add a reminder with where to check.</p>
      <ErrorNotice error={error} />
      <Actions saving={saving} back={back} canContinue={complete}
        onContinue={() => next(() => api.put<OnboardingState>("/api/onboarding/situation", {
          ...answers,
          dependants: { answer: answers.dependants.answer, ...(answers.dependants.answer === "yes" ? { count: answers.dependants.count ?? 1 } : {}) },
        }))} />
    </section>
  );
}

function Areas({ state, saving, next, back, error }: StepProps) {
  const [areas, setAreas] = useState<string[]>(state.areas);
  const toggle = (code: string) => setAreas(areas.includes(code) ? areas.filter((a) => a !== code) : [...areas, code]);
  return (
    <section className="stack" style={{ "--gap": "16px" } as React.CSSProperties}>
      <h1>What applies to you?</h1>
      <p className="muted">Tick everything that's part of your finances. We'll only show what's relevant, and you can add more any time.</p>
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
        {state.areaChoices.map((a) => (
          <button key={a.code} type="button" className="tile" aria-pressed={areas.includes(a.code)} onClick={() => toggle(a.code)}>
            <span className="title">{a.title}<span className="tick" aria-hidden>{areas.includes(a.code) ? "✓" : ""}</span></span>
            <span className="muted small">{a.description}</span>
          </button>
        ))}
      </div>
      <ErrorNotice error={error} />
      <Actions saving={saving} back={back} onContinue={() => next(() => api.put<OnboardingState>("/api/onboarding/areas", { areas }))} />
    </section>
  );
}

function Accountant({ state, saving, next, back, error }: StepProps) {
  const [name, setName] = useState(state.accountant?.name ?? "");
  const [firm, setFirm] = useState(state.accountant?.firm ?? "");
  const [email, setEmail] = useState(state.accountant?.email ?? "");
  return (
    <section className="stack" style={{ "--gap": "16px" } as React.CSSProperties}>
      <h1>Your accountant</h1>
      <p className="muted">Optional. At the end of the year we prepare a pack for them with everything they need.</p>
      <div className="field"><label htmlFor="acc-name">Name</label><input id="acc-name" type="text" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="field"><label htmlFor="acc-firm">Firm <span className="muted">(optional)</span></label><input id="acc-firm" type="text" value={firm} onChange={(e) => setFirm(e.target.value)} /></div>
      <div className="field"><label htmlFor="acc-email">Email <span className="muted">(optional)</span></label><input id="acc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
      <ErrorNotice error={error} />
      <Actions saving={saving} back={back} skip={() => next()} canContinue={Boolean(name.trim())}
        onContinue={() => next(() => api.put<OnboardingState>("/api/onboarding/accountant", {
          name: name.trim(), firm: firm.trim() || null, email: email.trim() || null,
        }))} />
    </section>
  );
}

function History({ saving, next, back }: StepProps) {
  return (
    <section className="stack" style={{ "--gap": "16px" } as React.CSSProperties}>
      <h1>Bring in past years</h1>
      <p className="muted">If you've lodged returns before, you'll be able to upload each year's tax return and notice of assessment. We'll record the year exactly as lodged and carry balances like capital losses forward.</p>
      <div className="notice">Importing past returns is coming in a later update. You can carry on now and add them when it's ready.</div>
      <Actions saving={saving} back={back} onContinue={() => next()} />
    </section>
  );
}

function Ready({ state, saving, back, error }: StepProps) {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const year = state.yearChoices.find((y) => y.fy === state.startYear);
  return (
    <section className="stack" style={{ "--gap": "16px" } as React.CSSProperties}>
      <h1>You're set up</h1>
      <div className="card flat stack">
        <div className="row"><span className="muted">Starting from</span><div className="spacer" /><strong>{year?.label ?? "—"}</strong></div>
        <div className="row"><span className="muted">Areas</span><div className="spacer" /><strong>{state.areas.length || "None yet"}</strong></div>
        <div className="row"><span className="muted">Accountant</span><div className="spacer" /><strong>{state.accountant?.name ?? "Not added"}</strong></div>
      </div>
      <p className="muted">Next, add the things you ticked and start capturing receipts. Your home screen shows what's left to set up.</p>
      <ErrorNotice error={failure ?? error} />
      <Actions saving={saving || busy} back={back} continueLabel="Go to Home" canContinue={Boolean(state.startYear)}
        onContinue={async () => {
          setBusy(true);
          try {
            await api.post("/api/onboarding/complete");
            await refresh();
            navigate("/", { replace: true });
          } catch (e) {
            setFailure(e);
          } finally {
            setBusy(false);
          }
        }} />
    </section>
  );
}
